import { getCurrentInstance, ref, Ref, watch } from 'vue';
import TablesService from '@/api/services/tables.service';
import { useUi } from '@/composition/use-ui';
import { ITable } from '@/interfaces/ITable';
import { useRestaurantStore } from '@/stores/restaurant.store';

export interface IUseMenuQrTables {
  /** null while the load for the current branch is in flight or has failed. */
  tables: Ref<ITable[] | null>;
  loading: Ref<boolean>;
  error: Ref<boolean>;
  reload(): Promise<void>;
}

/** Loads the selected branch's tables, ignoring responses of superseded loads. */
export function useMenuQrTables(): IUseMenuQrTables {
  const restaurantStore = useRestaurantStore();
  const ui = useUi();
  const proxy = getCurrentInstance()!.proxy;
  const tables = ref<ITable[] | null>(null);
  const loading = ref(false);
  const error = ref(false);
  let generation = 0;

  async function reload() {
    const restaurant = restaurantStore.restaurant;
    const branch = restaurantStore.selectedBranch;
    const current = ++generation;

    // Never keep another branch's tables around while the new ones load.
    tables.value = null;
    error.value = false;

    if (!restaurant || !branch) {
      loading.value = false;
      return;
    }

    loading.value = true;

    try {
      const loaded = await TablesService.getAll(restaurant.id, branch.id);

      if (current === generation) tables.value = loaded;
    } catch (loadError) {
      if (current === generation) {
        error.value = true;
        ui.serverError(loadError, String(proxy.$t('qr.tables.loadError')));
      }
    } finally {
      if (current === generation) loading.value = false;
    }
  }

  // Multi-source form: compared element-wise, so a store refresh with the same ids
  // does not reload (and reset the user's table selection).
  watch(
    [
      () => restaurantStore.restaurant?.id,
      () => restaurantStore.selectedBranch?.id,
    ],
    () => {
      reload();
    },
    { immediate: true }
  );

  return { tables, loading, error, reload };
}
